/**
 * Converts a hexadecimal color code to an RGB object.
 *
 * @param {string} hex - The hexadecimal color code to convert.
 * @returns {{r: number; g: number; b: number} | null} - An object representing the RGB values
 *    of the color, or null if the input is not a valid hexadecimal color code.
 */
export const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
	// Ensure the hex code starts with '#'
	if (hex.charAt(0) === '#') {
		hex = hex.slice(1);
	}

	// Parse short form like "#FFF"
	if (hex.length === 3) {
		const r = parseInt(hex.charAt(0) + hex.charAt(0), 16) / 255;
		const g = parseInt(hex.charAt(1) + hex.charAt(1), 16) / 255;
		const b = parseInt(hex.charAt(2) + hex.charAt(2), 16) / 255;

		return {r, g, b};
	}

	// Parse regular form like "#FFFFFF"
	if (hex.length === 6) {
		const r = parseInt(hex.substring(0, 2), 16) / 255;
		const g = parseInt(hex.substring(2, 4), 16) / 255;
		const b = parseInt(hex.substring(4, 6), 16) / 255;

		return {r, g, b};
	}

	return null;
};

/**
 * Determines whether a color represented by a hexadecimal code is considered light or not.
 *
 * @param {string} hex - The hexadecimal color code to evaluate.
 * @returns {boolean | null} - Returns `true` if the color is light, `false` if it's dark,
 *   or `null` if the input is not a valid hexadecimal color code.
 */
export const isLightColor = (hex: string): boolean | null => {
	if (hex) {
		const rgb = hexToRgb(hex);

		if (rgb) {
			const luminance = 0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b;

			return luminance > 0.5;
		}
	}

	return null;
};

/**
 * Validates if a string is a valid hexadecimal color code.
 * Accepts 3-digit (#RGB) and 6-digit (#RRGGBB) formats.
 *
 * @param {string | null | undefined} hexColor - The color string to validate
 * @returns {boolean} True if the color is a valid hex code
 */
export const isValidHexColor = (hexColor?: string | null): boolean => {
	if (!hexColor) {
		return false;
	}

	const hexRegex = /^#[0-9A-Fa-f]{3}$|^#[0-9A-Fa-f]{6}$/;

	return hexRegex.test(hexColor);
};
